import React, { useEffect, useMemo } from "react";
import store from "store";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import moment from "moment";
// mui
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import { ThemeProvider } from "@material-ui/core/styles";
// theme
import theme from "../../assets/v3/theme";
// apis
import {
  getRegistrationFormConfig,
  updateStudentDetails,
} from "../../apis/UserService";
// hooks
import useFetch from "../../hooks/useFetch";
import { useSnackbar } from "../../context/snackbar";
// fields
import { StudentDetailsFields } from "./utils";
import { useStyles } from ".";
import TextScroll from "./TextScroll";
// utils
import { renderFormField } from "./RenderFormField";
import { hostnamesMap } from "../../components/LogoURLMap";
import { Typography } from "@material-ui/core";

const PersonalDetails = () => {
  const classes = useStyles();
  const history = useHistory();
  const instituteId = hostnamesMap[window.location.hostname];
  const sid = store.get("user")._id;
  const page = "student-details";
  const [l1, e1, d1, f1, r1] = useFetch(getRegistrationFormConfig);
  const [l2, e2, d2, f2, r2] = useFetch(updateStudentDetails);
  const payload = d1 ? d1.payload : null;
  const fields = payload && payload.fields ? payload.fields : [];
  const details = d1 ? d1.details : {};
  const next = payload && payload.next ? payload.next : null;
  const scrollText =
    payload && payload.scroll_text ? payload.scroll_text : null;
  // const previous = payload ? payload.previous : null;
  const sysTime = d1 ? d1.sysTime : null;
  const { openSnackbar } = useSnackbar();

  const formatRouteName = (route) => {
    if (!route) return "";
    return route
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const fieldMap = useMemo(() => {
    return StudentDetailsFields.reduce((map, field) => {
      map[field.field] = field;
      return map;
    }, {});
  }, []);

  const initialValues = useMemo(() => {
    const vals = fields.reduce((values, item) => {
      const myKey =
        fieldMap && fieldMap[item.field] ? fieldMap[item.field].key : "";
      if (myKey && details) {
        values[item.field] = myKey.includes(".")
          ? details[myKey.split(".")[0]]
            ? details[myKey.split(".")[0]][myKey.split(".")[1]]
            : ""
          : details[myKey];

        if (myKey === "dateOfBirth" && values[item.field]) {
          values[item.field] = moment(values[item.field]).format("YYYY-MM-DD");
        }
      } else {
        values[item.field] = "";
      }
      if (item.field === "nationality" && values[item.field] === "") {
        values[item.field] = "India";
      }
      return values;
    }, {});
    // Ensure referenceDesc is set from details if present
    if (details && typeof details.referenceDesc !== "undefined") {
      vals.referenceDesc = details.referenceDesc;
    } else {
      vals.referenceDesc = "";
    }
    return vals;
  }, [fields, details]);

  useEffect(() => {
    f1({
      instituteId,
      page,
      sid,
    });
    window.scrollTo(0, 0);

    return () => {
      r1();
    };
  }, []);

  useEffect(() => {
    if (e1) {
      openSnackbar("Failed to fetch registration form config", "error");
    }
  }, [e1]);

  useEffect(() => {
    if (d2) {
      console.log("Form Submitted", d2);
      if (d2.data.status === "SUCCESS") {
        openSnackbar("Student details saved successfully", "success");
        if (next) {
          history.push(`${next}`);
        }
      } else {
        openSnackbar(d2.data.message, "warning", false);
      }
    }
    if (e2) {
      console.error("Failed to submit form", e2);
    }

    return () => {
      r2();
    };
  }, [d2, e2]);

  useEffect(() => {
    const datePicker = document.getElementById("dateOfBirth");
    if (datePicker) {
      datePicker.setAttribute("max", moment("2015-12-31").format("YYYY-MM-DD"));
      datePicker.setAttribute("min", moment("2003-01-01").format("YYYY-MM-DD"));
    }
  }, [sysTime]);

  const validationSchema = useMemo(() => {
    return Yup.object().shape(
      fields.reduce((schema, item) => {
        const field = fieldMap[item.field];
        if (!field) return schema; // guard against undefined field
        const cond = item.con;
        let validator = Yup.string();
        if (field.type === "email") {
          validator = validator.email("Invalid email format");
        }
        if (field.type === "text") {
          validator = validator.matches(
            /^[a-zA-Z\s]*$/,
            "Only alphabets and spaces are allowed"
          );
        }
        if (field.field === "fullName") {
          validator = validator
            .trim()
            .min(3, "Minimum 3 characters required")
            .max(28, "Maximum 28 characters allowed");
        }
        if (field.type === "number") {
          if (field.field === "aadharNo") {
            validator = validator.matches(
              /^[0-9]{12}$/,
              "Aadhar number must be 12 digits"
            );
          } else {
            validator = validator.matches(
              /^[0-9]*$/,
              "Only numbers are allowed"
            );
          }
        }
        if (
          field.type === "number" &&
          (field.field.toLowerCase().includes("father") ||
            field.field.toLowerCase().includes("mother"))
        ) {
          validator = validator.matches(
            /^[0-9]+$/,
            "Must be a valid phone number"
          );
        }
        if (field.type === "select") {
          validator = validator.oneOf(
            field.options,
            `Must be one of: ${field.options.join(", ")}`
          );
        }
        if (cond === "R") {
          validator = validator.required("Required");
        }
        schema[field.field] = validator;
        return schema;
      }, {})
    );
  }, [fields]);

  return (
    <ThemeProvider theme={theme}>
      {scrollText && (
        <TextScroll
          text={scrollText}
          textStyles={{
            fontWeight: "bolder",
            fontSize: "1.5rem",
            background: `-webkit-linear-gradient(#ED1B24, #074EA0)`,
            "-webkit-background-clip": "text",
            "-webkit-text-fill-color": "transparent",
          }}
        />
      )}
      <Box
        bgcolor={"background.default"}
        minHeight="100vh"
        width="100%"
        overflow="hidden"
        paddingTop={1}
      >
        <Container
          maxWidth="md"
          bgcolor={"background.default"}
          className={classes.formContainer}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                  const data = Object.keys(values).reduce((obj, f) => {
                    const myKey =
                      fieldMap && fieldMap[f] ? fieldMap[f].key : "";
                    if (myKey) {
                      obj[myKey] = values[f];
                    } else {
                      obj[f] = values[f];
                    }
                    return obj;
                  }, {});
                  // If howDoYouKnowAboutUs is not Reference, clear referenceDesc and send as empty string
                  if (values.howDoYouKnowAboutUs !== "Reference") {
                    data["referenceDesc"] = "";
                  } else if (
                    values.referenceDesc &&
                    values.referenceDesc.trim() !== ""
                  ) {
                    data["referenceDesc"] = values.referenceDesc;
                  }
                  f2({ sid, data, page });
                }}
                enableReinitialize={true}
              >
                {({ values, setFieldValue, touched, errors }) => (
                  <Form>
                    {/* Clear referenceDesc when howDoYouKnowAboutUs changes to non-Reference */}
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="h6">
                          <b>Student Details</b>
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          width={"100%"}
                          height="100%"
                          alignItems="center"
                        >
                          <Typography variant="caption">
                            next: <b>{formatRouteName(next)}</b>
                          </Typography>
                        </Box>
                      </Grid>
                      {fields.map((field) => {
                        if (!field) return null;
                        if (!field.field) return null;
                        if (!fieldMap[field.field]) return null;
                        const fieldDescription = fieldMap[field.field];
                        // Special handling for howDoYouKnowAboutUs to clear referenceDesc
                        if (field.field === "howDoYouKnowAboutUs") {
                          return (
                            <Grid item xs={12} sm={6} md={4} key={field.field}>
                              {renderFormField({
                                field: fieldDescription,
                                handleChange: (name, value) => {
                                  setFieldValue(name, value);
                                  if (value !== "Reference") {
                                    setFieldValue("referenceDesc", "");
                                  }
                                },
                                value: values[field.field],
                                error:
                                  touched[field.field] && errors[field.field],
                                helperText: errors[field.field],
                                optionClassName: classes.option,
                                labelClassName: classes.label,
                                required: field ? field.con === "R" : false,
                                readOnly: l1,
                                values,
                                setFieldValue,
                              })}
                            </Grid>
                          );
                        }
                        const inputs = {
                          field: fieldDescription,
                          handleChange: setFieldValue,
                          value: values[field.field],
                          error: touched[field.field] && errors[field.field],
                          helperText: errors[field.field],
                          optionClassName: classes.option,
                          labelClassName: classes.label,
                          required: field ? field.con === "R" : false,
                          readOnly: l1,
                          values,
                          setFieldValue,
                        };

                        if (
                          inputs &&
                          inputs.field &&
                          inputs.field.field === "admissionType"
                        ) {
                          if (
                            details &&
                            details.branch === "AM" &&
                            details.instituteId === "5fd25b12ed14200ff702f738"
                          ) {
                            inputs.field.options = ["Day scholars"];
                          }
                        }

                        return (
                          <Grid item xs={12} sm={6} md={4} key={field.field}>
                            {renderFormField(inputs)}
                          </Grid>
                        );
                      })}
                      {/* Show referenceDesc field only when howDoYouKnowAboutUs is Reference */}
                      {values.howDoYouKnowAboutUs === "Reference" && (
                        <Grid item xs={12} sm={6} md={4} key="referenceDesc">
                          {renderFormField({
                            field: {
                              field: "referenceDesc",
                              type: "text",
                              label: "Reference name & contact",
                            },
                            handleChange: setFieldValue,
                            value: values.referenceDesc,
                            error:
                              touched.referenceDesc && errors.referenceDesc,
                            helperText: errors.referenceDesc,
                            optionClassName: classes.option,
                            labelClassName: classes.label,
                            required: true,
                            readOnly: l1,
                            values,
                            setFieldValue,
                          })}
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="flex-end"
                          flexGrow={1}
                          width="100%"
                        >
                          {/* <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                              if (previous) {
                                history.push(`/${previous}`);
                              }
                            }}
                            disabled={l1 || l2}
                            className={classes.blueGradientBtn2}
                          >
                            Previous
                          </Button> */}
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={l1 || l2}
                            className={classes.blueGradientBtn}
                          >
                            {l2 ? "Saving..." : "Next"}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Form>
                )}
              </Formik>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default PersonalDetails;
